import { HeatmapLegend } from '@components/storeMap/HeatmapLegend'
import AgeShelf from '@components/storeMap/floor/2d/AgeShelf'
import CategoryShelf from '@components/storeMap/floor/2d/CategoryShelf'
import Floor, { type LayerProducer, type ShelfProducer } from '@components/storeMap/floor/2d/Floor'
import MarkedShelf from '@components/storeMap/floor/2d/MarkedShelf'
import OOSShelf from '@components/storeMap/floor/2d/OOSShelf'
import ShelfInfoPopover from '@components/storeMap/floor/2d/ShelfInfoPopover'
import UnknownPercentageShelf from '@components/storeMap/floor/2d/UnknownPercentagefShelf'
import CategoryLayer from '@components/storeMap/floor/2d/categoryGrouping/Layer'
import CategoryLayerDebug from '@components/storeMap/floor/2d/categoryGrouping/LayerDebug'
import { useStoreMapContext } from '@components/storeMap/provider/StoreMapContext'
import { isProdMode } from '@utils/env'

import type { TransformedShelf } from '@state/shelf/types'

const categoryLayerProducer: LayerProducer = () =>
  <CategoryLayer key='layer:category' />

const categoryLayerDebugProducer: LayerProducer = (shelves) =>
  isProdMode ? null : <CategoryLayerDebug key='layer:category:debug' shelves={shelves} />

function TwoDim() {
  const {
    highlightedShelves,
    setSelectedShelfId,
    activeLayers, isLayerActive,
    hoveredShelf, setHoveredShelf,
    shelfProducer2D, overlays2D,
  } = useStoreMapContext()

  function getShelfProducer() : ShelfProducer {
    if (shelfProducer2D) {
      return shelfProducer2D
    }

    return (shelf: TransformedShelf) => {
      if (isLayerActive('CATEGORY_GROUPING_DEBUG')) {
        return <CategoryShelf key={shelf.id} shelf={shelf} onHover={setHoveredShelf} />
      }

      if (isLayerActive('DATA_AGE')) {
        return <AgeShelf key={shelf.id} shelf={shelf} onHover={setHoveredShelf} />
      }

      if (isLayerActive('HIGHLIGHT_OUT_OF_SHELF')) {
        // can't be opened in scan view; in the day view shelf detail is how a neutral shelf gets explained
        return <OOSShelf key={shelf.id} shelf={shelf} onHover={setHoveredShelf} onShelfClick={shelf.dayState ? setSelectedShelfId : undefined} />
      }

      if (isLayerActive('HEATMAP_UNKNOWNS')) {
        return <UnknownPercentageShelf key={shelf.id} shelf={shelf} onHover={setHoveredShelf} />
      }

      return <MarkedShelf key={shelf.id} shelf={shelf} onHover={setHoveredShelf} />
    }
  }

  function getLayerProducers() : LayerProducer[] {
    const producers: LayerProducer[] = []

    //TODO: we should do this differently, align with 3d?
    if (overlays2D) {
      producers.push(...overlays2D.map(overlay => () => overlay))
    }

    if (isLayerActive('CATEGORY_GROUPING') || isLayerActive('CATEGORY_GROUPING_DEBUG')) {
      producers.push(categoryLayerProducer)
    }

    if (isLayerActive('CATEGORY_GROUPING_DEBUG')) {
      producers.push(categoryLayerDebugProducer)
    }

    return producers
  }

  return (
    <>
      <div className="flex size-full flex-col items-center justify-center">
        <Floor
          highlightedShelves={highlightedShelves}
          onShelfClick={setSelectedShelfId}
          shelfProducer={getShelfProducer()}
          layerProducers={getLayerProducers()}
        />
      </div>

      {isLayerActive('HEATMAP_UNKNOWNS') && <HeatmapLegend />}

      {hoveredShelf && <ShelfInfoPopover hoveredShelf={hoveredShelf} activeLayers={activeLayers} />}
    </>
  )
}

export default TwoDim